import { useProperty } from '@eti/providers';
import { date } from '@eti/utils';
import type { OriginalVersionType } from '../../../../models';
import type { TripSegment } from '../../../../../../widgets/trip-itinerary/models';
import ItineraryLocationTime from './ItineraryLocationTime';
import ItineraryTripSegmentInfo from './ItineraryTripSegmentInfo';
import ItineraryTripSegmentEvents from './ItineraryTripSegmentEvents';
import ItineraryChangeOfDate from './ItineraryChangeOfDate';

interface ItineraryTripSegmentProps {
  isLastSegment: boolean;
  originalSegment: NonNullable<OriginalVersionType>['originalSegments'][number] | null;
  segment: TripSegment;
  upgradeType?: string;
}

const ItineraryTripSegment = ({
  isLastSegment,
  originalSegment,
  segment,
  upgradeType,
}: ItineraryTripSegmentProps) => {
  const { p } = useProperty();

  const durationInDays = date.durationInDays(
    new Date(segment.departuredAt),
    new Date(segment.arrivedAt),
  );

  const isTechnicalStopsEnabled = p('IbeClient.TripDetails.TechnicalStops.Enabled');
  const shouldShowTechnicalStops = isTechnicalStopsEnabled && segment.technicalStops.length > 0;

  return (
    <div data-testid={`trip-itinerary-trip-segment-${segment.segmentId}`}>
      <ItineraryLocationTime
        airportCode={segment.origin.code}
        airportName={segment.origin.name}
        cityName={segment.origin.cityName}
        isStartingPoint
        key="itinerary-origin"
        originalTime={originalSegment?.departuredAt}
        time={segment.departuredAt}
      />
      <ItineraryTripSegmentInfo segment={segment} upgradeType={upgradeType} />
      {shouldShowTechnicalStops && (
        <ItineraryTripSegmentEvents
          originalArrivalDate={originalSegment?.arrivedAt}
          segment={segment}
        />
      )}
      {!shouldShowTechnicalStops && durationInDays > 0 && (
        <ItineraryChangeOfDate
          arrivalDate={segment.arrivedAt}
          durationInDays={durationInDays}
          isDisplayedFirst
          isDisplayedLast
          originalArrivalDate={originalSegment?.arrivedAt}
        />
      )}
      <ItineraryLocationTime
        airportCode={segment.destination.code}
        airportName={segment.destination.name}
        cityName={segment.destination.cityName}
        isLastStop={isLastSegment}
        key="itinerary-destination"
        originalTime={originalSegment?.arrivedAt}
        time={segment.arrivedAt}
      />
    </div>
  );
};

export default ItineraryTripSegment;
